import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArmGlyph, IyafWordmark } from "@/components/brand/IyafMark";
import { AnimatedCounter } from "@/components/ui-iyaf/AnimatedCounter";
import { arms, partners } from "@/data/content";
import ArmsNav from "@/components/site/ArmsNav";

const consult = arms.find((a) => a.key === "consult");

const CONSULT_METRICS = [
  { key: "engagements", v: "7" },
  { key: "impactHours", v: "4,000" },
  { key: "satisfaction", v: "100%" },
  { key: "profitReinvested", v: "100%" },
];

const SERVICE_KEYS = ["innovation", "evaluation", "research", "reporting"];

export default function Consult() {
  const { t } = useTranslation();
  const cycleSteps = t("armPages.consult.cycleSteps", { returnObjects: true });

  return (
    <div className="relative">
      <section className="pt-16 pb-12">
        <div className="container-iyaf grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <ArmGlyph color={consult.color} className="w-14 h-14" />
              <div>
                <p className="small-caps text-xs text-iyaf-gray">{t("armCards.active")}</p>
                <p className="text-sm text-iyaf-ink/55">{t("armCards.consult.tagline")}</p>
              </div>
            </div>
            <IyafWordmark variant="consult" size="hero" className="mb-6" />
            <h1 className="font-display font-black tracking-tighter leading-[0.95] max-w-3xl text-balance text-4xl sm:text-5xl">
              <span className="font-editorial italic font-normal text-iyaf-ink/85">{t("armPages.consult.heroTitle")}</span>
            </h1>
            <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
              {t("armPages.consult.heroBody")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-[40px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&w=1200"
                alt="Consulting session"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-iyaf">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5">
            {CONSULT_METRICS.map((m) => (
              <div key={m.key} className="bg-white p-6">
                <p className="font-display text-4xl font-extrabold text-iyaf-ink"><AnimatedCounter value={m.v} /></p>
                <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/55 mt-2">{t(`armPages.consult.metrics.${m.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-ink/55 mb-3">{t("armPages.consult.portfolioEyebrow")}</p>
          <h2 className="h2 mb-10">{t("armPages.consult.portfolioTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_KEYS.map((key, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="p-7 rounded-3xl bg-white border border-black/5"
                data-testid={`consult-service-${key}`}
              >
                <p className="font-display text-2xl font-bold">{t(`armPages.consult.services.${key}.name`)}</p>
                <ul className="mt-4 space-y-2">
                  {t(`armPages.consult.services.${key}.items`, { returnObjects: true }).map((it) => (
                    <li key={it} className="text-sm text-iyaf-ink/70 flex gap-2">
                      <span className="text-iyaf-gray">›</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-iyaf-ink text-white">
        <div className="container-iyaf">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow text-iyaf-orange mb-4">{t("armPages.consult.cycleEyebrow")}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
                {t("armPages.consult.cycleTitle")}{" "}
                <span className="font-editorial italic font-normal text-iyaf-orange">{t("armPages.consult.cycleTitleItalic")}</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-md">{t("armPages.consult.cycleBody")}</p>
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-3">
                {Array.isArray(cycleSteps) && cycleSteps.map((step, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10"
                  >
                    <span className="w-10 h-10 rounded-full bg-iyaf-orange text-iyaf-ink font-display font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-white/85">{step}</p>
                  </motion.li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-4">{t("armPages.consult.clientsEyebrow")}</p>
          <h2 className="h2 mb-10">{t("armPages.consult.clientsTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {partners.international.map((c) => (
              <a key={c.name} href={c.site} target="_blank" rel="noreferrer" className="group p-6 sm:p-8 rounded-3xl bg-white border border-black/5 flex flex-col items-center justify-center text-center hover:bg-iyaf-paper transition min-h-[140px]">
                <img
                  src={c.logo}
                  alt={c.name}
                  loading="lazy"
                  className="h-12 w-auto mb-3 object-contain grayscale opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <p className="font-display text-sm font-semibold leading-tight">{c.name}</p>
              </a>
            ))}
          </div>
          <div className="mt-10 p-8 rounded-3xl bg-iyaf-orange/10 border border-iyaf-orange/30">
            <p className="eyebrow text-iyaf-orange mb-3">{t("armPages.consult.savingsEyebrow")}</p>
            <p className="font-display text-3xl sm:text-4xl font-bold max-w-3xl">
              {t("armPages.consult.savingsBody")}
            </p>
          </div>
        </div>
      </section>

      <ArmsNav currentKey="consult" />
    </div>
  );
}
